import BaseModal from 'components/BaseModal'
import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { isValidEntityId } from 'entities-next/common/common.validations'
import { getApiScoutingCampaignHandlers } from 'entities-next/scoutingCampaign'
import React from 'react'
import { NumberFormatValues } from 'react-number-format'
import { useDispatch } from 'react-redux'
import SubmitButton from 'scenes/Dashboard/components/SubmitButton'
import { FastField, FormManager } from 'utils-next/forms'
import * as Yup from 'yup'

import { Button, TextField, Theme } from '@mui/material'
import { WithStyles, createStyles, withStyles } from '@mui/styles'

export type CreateDemoSpaceForCorporateModalProps = {
  corporateId: number
  onClose: () => unknown
} & WithStyles<typeof styles>

const CreateDemoSpaceForCorporateModal: React.FunctionComponent<CreateDemoSpaceForCorporateModalProps> = (props) => {
  const { onClose, classes } = props

  return (
    <BaseModal className={classes.modal} onClose={onClose} open={true} title={'Create a new Demo Space'}>
      <CreateDemoSpaceForCorporateForm {...props} corporateId={props.corporateId} />
    </BaseModal>
  )
}

type CreateDemoSpaceForCorporateFormProps = {
  corporateId: number
  onClose: () => void
  validationError?: string
} & WithStyles<typeof styles>

const CreateDemoSpaceForCorporateForm = (props: CreateDemoSpaceForCorporateFormProps) => {
  const { onClose, classes, corporateId } = props
  const { apiCreateDemoSpaceForCorporate } = getApiScoutingCampaignHandlers()

  const dispatch = useDispatch()

  async function handleSubmit() {
    apiCreateDemoSpaceForCorporate(corporateId)
      .then((demoSpace) => {
        dispatch(
          showFlashNotice({
            message: `Demo space with id ${demoSpace.id} has been created`,
            autoHide: true,
            variant: FlashNoticeVariant.success,
          })
        )
        onClose()
      })
      .catch(() => {
        dispatch(
          showFlashNotice({
            message: `Error creating demo space for corporate ${corporateId}`,
            autoHide: true,
            variant: FlashNoticeVariant.error,
          })
        )
        onClose()
      })
  }

  return (
    <FormManager
      initialValues={{ corporateId }}
      onSubmit={handleSubmit}
      validationSchema={CreateDemoSpaceForCorporateFormSchema}
    >
      {() => (
        <>
          <FastField
            component={TextField}
            label="Corporate ID"
            adornmentText={'Corporate'}
            placeholder="Corporate ID"
            name="corporateId"
            defaultValue={corporateId}
            internalInputProps={{
              thousandSeparator: '',
              isAllowed: ({ floatValue }: NumberFormatValues) =>
                floatValue === undefined || isValidEntityId(floatValue),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <div className={classes.formFooter}>
            <Button variant="text" size="medium" onClick={onClose}>
              Discard
            </Button>
            <SubmitButton label="Save" onClick={handleSubmit} />
          </div>
        </>
      )}
    </FormManager>
  )
}

const CreateDemoSpaceForCorporateFormSchema = Yup.object().shape({
  corporateId: Yup.number().required(),
})

const styles = ({ spacing }: Theme) =>
  createStyles({
    formFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    modal: {
      width: 700,
    },
    formField: {
      width: '70%',
      marginTop: spacing(1),
      marginBottom: spacing(3),
      WebkitAppearance: 'none',
    },
    errorMessage: {
      color: 'red',
    },
    confirmationMessage: {
      color: 'green',
    },
  })

CreateDemoSpaceForCorporateModal.displayName = 'CreateDemoSpaceForCorporateModal'

export default withStyles(styles)(CreateDemoSpaceForCorporateModal)
